import React, { useState, useMemo } from 'react';
import { useDeliverableAggregates } from '../../hooks/useDeliverableAggregates';
import { calculateDeliverablesTotals } from '../../services/deliverable.service';
import { formatCompactNumber } from '@/lib/utils/formatNumber';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Upload, Plus, Eye, Heart, MessageCircle, Share2, FileText, Sparkles, FileSpreadsheet, Download, Link2 } from 'lucide-react';
import { DeliverableRow } from './DeliverableRow';
import { AddPostModal, useScrapeCampaignDeliverables } from '@/domains/content';
import { ExportDeliverablesModal } from '../modals/ExportDeliverablesModal';
import { ExportButton } from '@/shared/ui/export/ExportButton';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { UploadUrlsModal } from '../modals/UploadUrlsModal';

export interface DeliverablesSectionProps {
  campaignId: string;
}

export const DeliverablesSection: React.FC<DeliverablesSectionProps> = ({ campaignId }) => {
  const { data: aggregates = [], isLoading, error } = useDeliverableAggregates(campaignId);
  const { scrapeCampaign, isScraping } = useScrapeCampaignDeliverables(campaignId);
  const [addPostModal, setAddPostModal] = useState<{ open: boolean; campaignInfluencerId: string; influencerName: string }>({
    open: false,
    campaignInfluencerId: '',
    influencerName: '',
  });
  const [uploadUrlsOpen, setUploadUrlsOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);


  // Calculate campaign totals from aggregates
  const campaignTotals = useMemo(() => {
    return calculateDeliverablesTotals(aggregates);
  }, [aggregates]);

  // Aggregate influencers already includes all campaign influencers via SQL LEFT JOIN
  const availableInfluencers = useMemo(() => {
    return aggregates.map((agg) => ({
      campaign_influencer_id: agg.campaign_influencer_id,
      influencer_name: agg.influencer_name || agg.influencer_handle || 'Influencer',
    }));
  }, [aggregates]);

  const handleAddPost = (campaignInfluencerId: string, influencerName: string) => {
    setAddPostModal({ open: true, campaignInfluencerId, influencerName });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-bold font-['Hanken_Grotesk',sans-serif]">Deliverables</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <p>Failed to load deliverables data</p>
      </div>
    );
  }

  if (aggregates.length === 0) {
    return (
      <div className="space-y-6">
        {/* Header with Upload URLs action button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-2xl font-bold text-slate-900 font-['Hanken_Grotesk',sans-serif]">Deliverables</h3>
            <p className="text-slate-500 text-sm mt-1">
              Track content performance and deliverables across all influencers.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setUploadUrlsOpen(true)}
              className="border-[#E9D5FF] hover:border-[#D8B4FE] text-[#A655F8] rounded-xl px-4 py-2 flex items-center gap-2 font-semibold shadow-sm transition-all"
            >
              <Link2 className="w-4 h-4 text-[#A655F8]" />
              Upload Links
            </Button>
          </div>
        </div>

        {/* Empty state card */}
        <div className="p-12 text-center font-['Hanken_Grotesk',sans-serif] bg-white border border-slate-200 rounded-2xl shadow-xs">
          <div className="w-12 h-12 rounded-full bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] mx-auto mb-4 border border-[#E9D5FF]">
            <Link2 className="w-6 h-6 stroke-[2]" />
          </div>
          <h4 className="text-lg font-bold text-slate-800 mb-1">
            No influencers assigned to this campaign yet
          </h4>
          <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
            Upload reel URLs to automatically identify creators, add them to your campaign, and start tracking deliverables.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Button
              onClick={() => setUploadUrlsOpen(true)}
              className="bg-[#A655F8] hover:bg-[#9042E0] text-white rounded-xl px-5 py-2 flex items-center gap-2 font-semibold shadow-sm"
            >
              <Link2 className="w-4 h-4 text-white" />
              Upload Links
            </Button>
          </div>
        </div>

        <UploadUrlsModal
          open={uploadUrlsOpen}
          onOpenChange={setUploadUrlsOpen}
          campaignId={campaignId}
          onSelectAddPost={() => {
            setUploadUrlsOpen(false);
            setAddPostModal({ open: true, campaignInfluencerId: '', influencerName: '' });
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Export and Upload Links action buttons */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-2xl font-bold text-slate-900 font-['Hanken_Grotesk',sans-serif]">Deliverables</h3>
          <p className="text-slate-500 text-sm mt-1">
            Track content performance and deliverables across all influencers.
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 flex-wrap">
          <Button
            variant="outline"
            onClick={scrapeCampaign}
            disabled={isScraping || aggregates.length === 0}
            className="border-slate-200 hover:border-slate-300 text-slate-700 rounded-xl px-4 py-2 flex items-center gap-2 font-semibold shadow-sm transition-colors"
          >
            {isScraping ? (
              <LoadingSpinner size="small" />
            ) : (
              <Download className="w-4 h-4 text-slate-600" />
            )}
            {isScraping ? "Refreshing Metrics…" : "Refresh Metrics"}
          </Button>
          <ExportButton
            variant="purple"
            iconType="spreadsheet"
            onClick={() => setExportModalOpen(true)}
            disabled={aggregates.length === 0}
          />
          <Button
            onClick={() => setUploadUrlsOpen(true)}
            className="bg-[#A655F8] hover:bg-[#9042E0] text-white rounded-xl px-4 py-2 flex items-center gap-2 font-semibold shadow-sm border border-transparent transition-colors"
          >
            <Link2 className="w-4 h-4 text-white" />
            Upload Links
          </Button>
        </div>
      </div>

      {/* Auto-fetch Performance Metrics Banner */}
      <div className="relative border border-slate-200 rounded-[16px] bg-[linear-gradient(100deg,#ffffff_0%,#ffffff_60%,#faf8ff_100%)] flex items-center justify-between font-['Hanken_Grotesk',sans-serif] shadow-sm overflow-hidden min-h-[140px]">
        {/* Left Content */}
        <div className="p-6 flex items-start gap-4 z-10 max-w-[55%] md:max-w-[65%]">
          <div className="w-12 h-12 rounded-[12px] bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] shrink-0 border border-[#A655F8]/5">
            <Sparkles className="w-6 h-6 stroke-[2]" />
          </div>
          <div className="space-y-1">
            <h4 className="text-[16px] font-semibold text-slate-800">Auto-fetch performance metrics</h4>
            <p className="text-[14px] text-slate-400 font-normal leading-normal">
              Link a public post and we'll automatically fetch views, likes, comments, shares and more.
            </p>
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              className="text-[#A655F8] text-[14px] font-medium flex items-center gap-1 mt-1.5 transition-colors cursor-pointer w-fit"
            >
              Learn more
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="stroke-[2.5] ml-0.5">
                <path d="M5 12h14" />
                <path d="m12 5 7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>

        {/* Right Image Illustration */}
        <div className="absolute right-0 bottom-0 top-0 w-[50%] md:w-[45%] pointer-events-none overflow-hidden flex items-center justify-end">
          <img
            src="/images/Deliverables.png"
            alt="Deliverables performance tracking"
            className="h-full object-contain object-right"
          />
        </div>
      </div>

      {/* Campaign Totals */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Total Posts */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm h-[134px] flex flex-col justify-between font-['Hanken_Grotesk',sans-serif]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#FAF5FF] flex items-center justify-center text-[#A655F8] flex-shrink-0">
              <FileText className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-slate-500 font-medium text-[14px] leading-tight tracking-wide">
                Total Posts
              </div>
              <div className="text-[30px] font-extrabold text-slate-900 mt-1 tracking-tight">
                {campaignTotals.posts}
              </div>
            </div>
          </div>
          <div className="text-slate-400 text-[12px] font-normal leading-none">
            Across all influencers
          </div>
        </div>

        {/* Total Views */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm h-[134px] flex flex-col justify-between font-['Hanken_Grotesk',sans-serif]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0">
              <Eye className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-slate-500 font-medium text-[14px] leading-tight tracking-wide">
                Total Views
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-[30px] font-extrabold text-slate-900 tracking-tight">
                  {formatCompactNumber(campaignTotals.views)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Total Likes */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm h-[134px] flex flex-col justify-between font-['Hanken_Grotesk',sans-serif]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-rose-50 flex items-center justify-center text-rose-600 flex-shrink-0">
              <Heart className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-slate-500 font-medium text-[14px] leading-tight tracking-wide">
                Total Likes
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-[30px] font-extrabold text-slate-900 tracking-tight">
                  {formatCompactNumber(campaignTotals.likes)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Total Comments */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm h-[134px] flex flex-col justify-between font-['Hanken_Grotesk',sans-serif]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-orange-50 flex items-center justify-center text-orange-600 flex-shrink-0">
              <MessageCircle className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-slate-500 font-medium text-[14px] leading-tight tracking-wide">
                Total Comments
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-[30px] font-extrabold text-slate-900 tracking-tight">
                  {formatCompactNumber(campaignTotals.comments)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Total Engagement */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm h-[134px] flex flex-col justify-between font-['Hanken_Grotesk',sans-serif]">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 flex-shrink-0">
              <Share2 className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-slate-500 font-medium text-[14px] leading-tight tracking-wide">
                Total Engagement
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-[30px] font-extrabold text-slate-900 tracking-tight">
                  {formatCompactNumber(campaignTotals.engagement)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Influencers Table */}
      <div className="border border-[#E5E7EB] rounded-xl overflow-hidden shadow-sm bg-white font-['Hanken_Grotesk',sans-serif]">
        <Table className="table-fixed">
          <colgroup>
            <col className="w-[19%]" />
            <col className="w-[8%]" />
            <col className="w-[8%]" />
            <col className="w-[8%]" />
            <col className="w-[9%]" />
            <col className="w-[8%]" />
            <col className="w-[9%]" />
            <col className="w-[11%]" />
            <col className="w-[10%]" />
            <col className="w-[9%]" />
            <col className="w-[9%]" />
          </colgroup>
          <TableHeader className="bg-[#F9FAFB]">
            <TableRow className="border-b border-[#E5E7EB] hover:bg-[#F9FAFB]/50">
              <TableHead className="text-slate-500 font-semibold text-xs uppercase tracking-wider">INFLUENCER</TableHead>
              <TableHead className="text-slate-500 font-semibold text-xs text-center uppercase tracking-wider">TYPE</TableHead>
              <TableHead className="text-slate-500 font-semibold text-xs text-center uppercase tracking-wider">VIEWS</TableHead>
              <TableHead className="text-slate-500 font-semibold text-xs text-center uppercase tracking-wider">LIKES</TableHead>
              <TableHead className="text-slate-500 font-semibold text-xs text-center uppercase tracking-wider">COMMENTS</TableHead>
              <TableHead className="text-slate-500 font-semibold text-xs text-center uppercase tracking-wider">SHARES</TableHead>
              <TableHead className="text-slate-500 font-semibold text-xs text-center uppercase tracking-wider">POST ENG.</TableHead>
              <TableHead className="text-slate-500 font-semibold text-xs text-center uppercase tracking-wider">ER% Followers</TableHead>
              <TableHead className="text-slate-500 font-semibold text-xs text-center uppercase tracking-wider">ER% Views</TableHead>
              <TableHead className="text-slate-500 font-semibold text-xs text-center uppercase tracking-wider">VIEW RATE</TableHead>
              <TableHead className="text-slate-500 font-semibold text-xs text-center uppercase tracking-wider">ACTIONS</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {aggregates.map((aggregate) => (
              <DeliverableRow
                key={aggregate.campaign_influencer_id}
                aggregate={aggregate}
                onAddPost={handleAddPost}
              />
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add Post Modal */}
      <AddPostModal
        open={addPostModal.open}
        onOpenChange={(open) => setAddPostModal((prev) => ({ ...prev, open }))}
        campaignInfluencerId={addPostModal.campaignInfluencerId}
        influencerName={addPostModal.influencerName}
        availableInfluencers={availableInfluencers}
        onBack={() => {
          setAddPostModal((prev) => ({ ...prev, open: false }));
          setUploadUrlsOpen(true);
        }}
      />

      {/* Export Deliverables to Google Sheets Modal */}
      <ExportDeliverablesModal
        open={exportModalOpen}
        onOpenChange={setExportModalOpen}
        campaignId={campaignId}
        totalCount={aggregates.length}
      />

      {/* Upload Links Modal (Add URL, Upload CSV, Add Post) */}
      <UploadUrlsModal
        open={uploadUrlsOpen}
        onOpenChange={setUploadUrlsOpen}
        campaignId={campaignId}
        onSelectAddPost={() => {
          setUploadUrlsOpen(false);
          setAddPostModal({ open: true, campaignInfluencerId: '', influencerName: '' });
        }}
      />
    </div>
  );
};
